import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { BONUS_POINT_OPTIONS } from "@shared/exam";
import type { Lang, SessionUser } from "@shared/api";
import { ME_QUERY_KEY, useAuth, useAuthProviders } from "@client/app/auth";
import { api } from "@client/api/client";
import { Button, Card, Input, Modal, PageHeader, Select, useToast } from "@client/components/ui";
import { LANGUAGE_SWITCH_ENABLED, useLang, useT, type CoreKey } from "@client/i18n";
import { fieldErrors, isCode, messageForError } from "@client/lib/errors";
import { formatNumber } from "@client/lib/format";
import { REOPEN_HREF } from "./onboarding/KomIgangCard";

/**
 * The closed set of `?google=<reason>` values the server redirects here with
 * (spec §10). `reauthed` has no text: it opens the delete confirmation. Anything
 * else is ignored; nothing from the URL is ever shown.
 */
const GOOGLE_REASONS = {
  cancelled: "auth.google.cancelled",
  expired: "auth.google.expired",
  failed: "auth.google.failed",
  reauthMismatch: "settings.google.reauthMismatch",
  ratelimited: "auth.google.rateLimited",
} as const satisfies Record<string, CoreKey>;

type GoogleReason = keyof typeof GOOGLE_REASONS;

function isGoogleReason(value: string | null): value is GoogleReason {
  return value !== null && Object.hasOwn(GOOGLE_REASONS, value);
}

const REAUTH_HREF = "/api/auth/google/start?intent=reauth";

export function SettingsPage() {
  const t = useT();
  const { lang } = useLang();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, updateMe, deleteMe, setLanguage } = useAuth();
  const providers = useAuthProviders();

  const [newCardsPerDay, setNewCardsPerDay] = useState(String(user?.newCardsPerDay ?? 20));
  const [bonusPoints, setBonusPoints] = useState(String(user?.bonusPoints ?? 0));
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [teacherCode, setTeacherCode] = useState("");
  const [teacherError, setTeacherError] = useState<unknown>(null);
  const [becoming, setBecoming] = useState(false);

  // A known `google=` reason is kept in state and the parameter is removed
  // once, so a reload does not repeat the message. `reauthed` (back from
  // Google to delete the account) opens the confirmation straight away.
  const initialGoogle = new URLSearchParams(location.search).get("google");
  const [reason, setReason] = useState<GoogleReason | null>(() =>
    isGoogleReason(initialGoogle) ? initialGoogle : null,
  );
  const [confirmOpen, setConfirmOpen] = useState(initialGoogle === "reauthed");
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("google")) return;
    const raw = params.get("google");
    if (isGoogleReason(raw)) setReason(raw);
    if (raw === "reauthed") setConfirmOpen(true);
    params.delete("google");
    const rest = params.toString();
    navigate(
      { pathname: location.pathname, search: rest ? `?${rest}` : "", hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location, navigate]);

  const showTeacherCard = user?.role === "student" && providers.data?.teacherCodeEnabled === true;
  const teacherFieldError =
    fieldErrors(teacherError, t).teacherCode ??
    (isCode(teacherError, "RATE_LIMITED") ? t("auth.code.rateLimited") : undefined);

  async function save() {
    setSaving(true);
    try {
      await updateMe({
        displayName: displayName.trim(),
        newCardsPerDay: Number(newCardsPerDay),
        bonusPoints: Number(bonusPoints),
      });
      toast.success(t("common.saved"));
    } catch (error) {
      toast.error(messageForError(error, t));
    } finally {
      setSaving(false);
    }
  }

  async function becomeTeacher(event: FormEvent) {
    event.preventDefault();
    setBecoming(true);
    setTeacherError(null);
    try {
      const { user: updated } = await api.post<{ user: SessionUser }>("/api/auth/teacher-code", {
        teacherCode: teacherCode.trim(),
      });
      queryClient.setQueryData(ME_QUERY_KEY, updated);
    } catch (error) {
      setTeacherError(error);
      if (!fieldErrors(error, t).teacherCode && !isCode(error, "RATE_LIMITED")) {
        toast.error(messageForError(error, t));
      }
    } finally {
      setBecoming(false);
    }
  }

  async function confirmDelete() {
    setDeleting(true);
    try {
      await deleteMe();
      toast.success(t("settings.deleted"));
      navigate("/", { replace: true });
    } catch (error) {
      if (isCode(error, "UNAUTHENTICATED")) {
        // The session is older than 10 minutes: prove it is still the same
        // person with Google (a top-level navigation, spec §7.4).
        setConfirmOpen(false);
        setNeedsReauth(true);
      } else {
        toast.error(messageForError(error, t));
      }
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <PageHeader title={t("settings.title")} />
      {reason && (
        <p role="alert" className="mb-6 text-sm text-red-700 dark:text-red-300">
          {t(GOOGLE_REASONS[reason])}
        </p>
      )}
      <div className="flex flex-col gap-6">
        <Card title={t("settings.account")}>
          <div className="flex flex-col gap-4">
            <Input
              label={t("auth.displayName")}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <Input label={t("auth.email")} value={user?.email ?? ""} disabled readOnly />
            {LANGUAGE_SWITCH_ENABLED && (
              <Select
                label={t("common.language")}
                value={lang}
                options={[
                  { value: "sv", label: "Svenska" },
                  { value: "en", label: "English" },
                ]}
                onChange={(event) => setLanguage(event.target.value as Lang)}
              />
            )}
          </div>
        </Card>

        <Card title={t("settings.preferences")}>
          <div className="flex flex-col gap-4">
            <Input
              label={t("settings.newCardsPerDay")}
              type="number"
              min={0}
              max={200}
              value={newCardsPerDay}
              onChange={(event) => setNewCardsPerDay(event.target.value)}
            />
            <Select
              label={t("settings.bonusPoints")}
              hint={t("settings.bonusHelp")}
              value={bonusPoints}
              options={BONUS_POINT_OPTIONS.map((points) => ({
                value: String(points),
                label: formatNumber(points, lang, { maxDecimals: 1 }),
              }))}
              onChange={(event) => setBonusPoints(event.target.value)}
            />
            <div>
              <Button onClick={save} loading={saving}>
                {saving ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </div>
        </Card>

        <Card title={t("settings.onboarding.title")} description={t("settings.onboarding.body")}>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link
              to={REOPEN_HREF}
              className="font-medium text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
            >
              {t("settings.onboarding.edit")}
            </Link>
            <Link
              to="/hjalp"
              className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
            >
              {t("settings.onboarding.help")}
            </Link>
          </div>
        </Card>

        {showTeacherCard && (
          <Card title={t("settings.teacher.title")}>
            <form onSubmit={becomeTeacher} className="flex flex-col gap-4" noValidate>
              <Input
                label={t("auth.teacherCode")}
                name="teacherCode"
                autoComplete="off"
                value={teacherCode}
                error={teacherFieldError}
                onChange={(event) => setTeacherCode(event.target.value)}
              />
              <div>
                <Button type="submit" loading={becoming}>
                  {t("settings.teacher.submit")}
                </Button>
              </div>
            </form>
          </Card>
        )}

        <Card title={t("settings.dangerZone")} description={t("settings.deleteWarning")}>
          <div className="flex flex-col items-start gap-4">
            {needsReauth && (
              <>
                <p role="alert" className="text-sm text-ink-700 dark:text-ink-300">
                  {t("settings.google.reauth")}
                </p>
                <a
                  href={REAUTH_HREF}
                  className="inline-flex h-10 items-center rounded-lg border border-ink-300 bg-white px-4 text-sm font-medium text-ink-800 hover:bg-ink-100 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-100 dark:hover:bg-ink-800"
                >
                  {t("settings.google.reauthButton")}
                </a>
              </>
            )}
            <Button variant="danger" onClick={() => setConfirmOpen(true)}>
              {t("settings.deleteAccount")}
            </Button>
          </div>
        </Card>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t("settings.deleteAccount")}
        description={t("settings.deleteWarning")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" loading={deleting} onClick={confirmDelete}>
              {t("common.delete")}
            </Button>
          </>
        }
      />
    </>
  );
}

export default SettingsPage;
